/**
 * DbInstancePanel — reusable správa DBHost instance.
 *
 * Obsahuje záložky: Databáze | Přihlášení | SQL Exec | Logy
 * Embeddable do DBHost stránky, Orchestrátoru, AppHost detailu atd.
 *
 * Props:
 *   instance   — název DB instance (dbhost_instances.name)
 *   engine     — 'postgres' | 'mariadb'
 *   targetNode — volitelný ACM node (name/label/IPv6); prázdné = lokální
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../lib/mcp';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Modal,
  RefreshButton,
  Select,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
} from './ui';
import {
  AlertTriangle,
  BarChart3,
  Copy,
  Database,
  Key,
  Plus,
  Settings,
  Terminal,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { MariaDbPerfPanel } from './MariaDbPerfPanel';
import { themedConfirm } from '../lib/themed-dialogs';

// ── Types ─────────────────────────────────────────────────────────────────────

type DbEngine = 'postgres' | 'mariadb';

interface DbDatabase {
  id: string;
  db_name: string;
  owner: string | null;
  created_at: string;
}

interface DbCredential {
  id: string;
  username: string;
  db_name: string | null;
  privileges: string;
  entity_id?: string | null;
  entity_label?: string | null;
  created_at: string;
}

interface DbDetail {
  db_name: string;
  engine: DbEngine;
  size?: string;
  encoding?: string;
  collation?: string;
  charset?: string;
  owner?: string;
  tables?: number;
}

interface DbCredAudit {
  ok: boolean;
  linked: { username: string; entity_id: string; entity_label: string; orphan_link: boolean }[];
  unlinked_in_container: { username: string; hosts: string[] }[];
  stale_links: { username: string; entity_label: string }[];
}

// ── Databases Panel ───────────────────────────────────────────────────────────

function DatabasesPanel(
  { instance, engine, targetNode }: { instance: string; engine: DbEngine; targetNode?: string },
) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newDb, setNewDb] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [settingsDb, setSettingsDb] = useState<string | null>(null);

  const extra = targetNode ? { target_node: targetNode } : {};

  const { data, isLoading } = useQuery({
    queryKey: ['dbhost-databases', instance, targetNode],
    queryFn: () =>
      mcpCall<{ ok: boolean; databases: DbDatabase[] }>(
        'dbhost.databases', { instance, action: 'list', ...extra },
      ),
  });

  const createMut = useMutation({
    mutationFn: () =>
      mcpCall('dbhost.databases', {
        instance,
        action: 'create',
        db_name: newDb,
        owner: newOwner || undefined,
        ...extra,
      }),
    onSuccess: () => {
      toast.success(t('dbhost.dbCreated', 'DB "{{name}}" vytvořena', { name: newDb }));
      setNewDb('');
      setNewOwner('');
      setCreating(false);
      qc.invalidateQueries({ queryKey: ['dbhost-databases', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-status', instance] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const dropMut = useMutation({
    mutationFn: (dbName: string) =>
      mcpCall('dbhost.databases', { instance, action: 'drop', db_name: dbName, ...extra }),
    onSuccess: (_, dbName) => {
      toast.success(t('dbhost.dbDeleted', 'DB "{{name}}" smazána', { name: dbName }));
      qc.invalidateQueries({ queryKey: ['dbhost-databases', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-status', instance] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm text-gray-400'>
          {t('dbhost.databasesCount', '{{count}} databází', { count: data?.databases?.length ?? 0 })}
        </span>
        <Button variant='primary' icon={<Plus size={15} />} onClick={() => setCreating(true)}>
          {t('dbhost.newDatabase', 'Nová databáze')}
        </Button>
      </div>

      {creating && (
        <Card>
          <CardContent className='p-4 space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>{t('dbhost.name', 'Název *')}</Label>
                <Input
                  size='sm'
                  value={newDb}
                  onChange={(e) => setNewDb(e.target.value)}
                  placeholder='myapp_db'
                />
              </div>
              {engine === 'postgres' && (
                <div>
                  <Label>{t('dbhost.ownerOptional', 'Owner (volitelně)')}</Label>
                  <Input
                    size='sm'
                    value={newOwner}
                    onChange={(e) => setNewOwner(e.target.value)}
                    placeholder='myapp_user'
                  />
                </div>
              )}
            </div>
            <div className='flex gap-2 justify-end'>
              <Button
                variant='ghost'
                onClick={() => {
                  setCreating(false);
                  setNewDb('');
                }}
              >
                {t('common.cancel', 'Zrušit')}
              </Button>
              <Button
                variant='primary'
                onClick={() => createMut.mutate()}
                disabled={!newDb}
                loading={createMut.isPending}
              >
                {createMut.isPending
                  ? t('dbhost.creating', 'Vytvářím…')
                  : t('ui.create', 'Vytvořit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className='border border-gray-800 rounded-xl overflow-hidden'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b border-gray-800 bg-gray-900/30'>
              <th className='text-left px-4 py-2.5 text-xs text-gray-600'>
                {t('dbhost.databaseCol', 'Databáze')}
              </th>
              {engine === 'postgres' && (
                <th className='text-left px-4 py-2.5 text-xs text-gray-600'>
                  {t('dbhost.ownerCol', 'Owner')}
                </th>
              )}
              <th className='text-left px-4 py-2.5 text-xs text-gray-600'>
                {t('dbhost.createdCol', 'Vytvořena')}
              </th>
              <th className='px-4 py-2.5 w-16'></th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-800/40'>
            {isLoading && (
              <tr>
                <td colSpan={4} className='px-4 py-3'>
                  <div className='space-y-2'>
                    <div className='h-6 bg-gray-800/40 rounded-xl animate-pulse' />
                    <div className='h-6 bg-gray-800/40 rounded-xl animate-pulse' />
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && (data?.databases?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={4}>
                  <EmptyState
                    icon={<Database size={24} />}
                    title={t('dbhost.noDatabases', 'Žádné databáze')}
                    className='py-8'
                  />
                </td>
              </tr>
            )}
            {data?.databases?.map((db) => (
              <tr key={db.id} className='group hover:bg-gray-800/20'>
                <td className='px-4 py-2.5'>
                  <code className='text-sm font-mono text-white'>{db.db_name}</code>
                </td>
                {engine === 'postgres' && (
                  <td className='px-4 py-2.5 text-xs text-gray-500'>{db.owner ?? '—'}</td>
                )}
                <td className='px-4 py-2.5 text-xs text-gray-600'>
                  {new Date(db.created_at).toLocaleDateString(i18n.language)}
                </td>
                <td className='px-4 py-2.5'>
                  <div className='opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 justify-end'>
                    <button
                      onClick={() => setSettingsDb(db.db_name)}
                      title={t('dbhost.dbSettings', 'Nastavení databáze')}
                      className='p-1 text-gray-600 hover:text-blue-400 transition-colors'
                    >
                      <Settings size={13} />
                    </button>
                    <button
                      onClick={async () =>
                        await themedConfirm(
                          t('dbhost.confirmDeleteDb', 'Smazat databázi "{{name}}"?', { name: db.db_name }),
                        ) && dropMut.mutate(db.db_name)}
                      disabled={dropMut.isPending}
                      title={t('dbhost.deleteDatabase', 'Smazat databázi')}
                      className='p-1 text-gray-600 hover:text-red-400 transition-colors'
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {settingsDb && (
        <DbSettingsModal
          instance={instance}
          engine={engine}
          dbName={settingsDb}
          extra={extra}
          onClose={() => setSettingsDb(null)}
        />
      )}
    </div>
  );
}

// ── DB Settings Modal ─────────────────────────────────────────────────────────

function DbSettingsModal({
  instance,
  engine,
  dbName,
  extra,
  onClose,
}: {
  instance: string;
  engine: DbEngine;
  dbName: string;
  extra: Record<string, unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [owner, setOwner] = useState('');
  const [charset, setCharset] = useState('');
  const [collation, setCollation] = useState('');

  const detailQ = useQuery({
    queryKey: ['dbhost-db-detail', instance, dbName],
    queryFn: () =>
      mcpCall<{ ok: boolean; detail: DbDetail }>(
        'dbhost.databases', { instance, action: 'detail', db_name: dbName, ...extra },
      ),
  });

  const detail = detailQ.data?.detail;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['dbhost-databases', instance] });
    qc.invalidateQueries({ queryKey: ['dbhost-db-detail', instance, dbName] });
  };

  const alterMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      mcpCall('dbhost.databases', {
        instance,
        action: 'alter',
        db_name: dbName,
        ...payload,
        ...extra,
      }),
    onSuccess: () => {
      toast.success(t('dbhost.dbUpdated', 'DB "{{name}}" upravena', { name: dbName }));
      setOwner('');
      setCharset('');
      setCollation('');
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const dropMut = useMutation({
    mutationFn: () =>
      mcpCall('dbhost.databases', {
        instance,
        action: 'drop',
        db_name: dbName,
        ...extra,
      }),
    onSuccess: () => {
      toast.success(t('dbhost.dbDeleted', 'DB "{{name}}" smazána', { name: dbName }));
      qc.invalidateQueries({ queryKey: ['dbhost-databases', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-status', instance] });
      onClose();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const kvRow = (label: string, value: string | number | null | undefined) => (
    <div className='flex justify-between text-xs'>
      <span className='text-gray-500'>{label}</span>
      <span className='text-gray-300 font-mono'>{value ?? '—'}</span>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('dbhost.dbSettingsNamed', 'Nastavení databáze: {{name}}', { name: dbName })}
      size='md'
    >
      <div className='space-y-5'>
        {/* Detail */}
        {detailQ.isLoading
          ? (
            <div className='space-y-2'>
              <div className='h-4 bg-gray-800/40 rounded-xl animate-pulse' />
              <div className='h-4 bg-gray-800/40 rounded-xl animate-pulse' />
              <div className='h-4 bg-gray-800/40 rounded-xl animate-pulse' />
            </div>
          )
          : (
            <div className='space-y-1.5'>
              {kvRow(t('dbhost.size', 'Velikost'), detail?.size)}
              {engine === 'postgres'
                ? (
                  <>
                    {kvRow(t('dbhost.ownerCol', 'Owner'), detail?.owner)}
                    {kvRow(t('dbhost.encoding', 'Encoding'), detail?.encoding)}
                    {kvRow(t('dbhost.collation', 'Collation'), detail?.collation)}
                  </>
                )
                : (
                  <>
                    {kvRow(t('dbhost.tables', 'Tabulky'), detail?.tables)}
                    {kvRow(t('dbhost.charset', 'Charset'), detail?.charset)}
                    {kvRow(t('dbhost.collation', 'Collation'), detail?.collation)}
                  </>
                )}
            </div>
          )}

        {/* Upravit */}
        <div className='space-y-3'>
          <div className='text-[10px] font-semibold text-gray-600 uppercase tracking-widest'>
            {t('common.edit', 'Upravit')}
          </div>
          {engine === 'postgres'
            ? (
              <div className='space-y-3'>
                <div>
                  <Label>{t('dbhost.ownerCol', 'Owner')}</Label>
                  <Input
                    size='sm'
                    value={owner}
                    onChange={(e) => setOwner(e.target.value)}
                    placeholder={detail?.owner ?? t('dbhost.newOwnerPh', 'nový owner')}
                  />
                </div>
                <Button
                  variant='primary'
                  disabled={!owner}
                  loading={alterMut.isPending}
                  onClick={() => alterMut.mutate({ owner })}
                >
                  {t('dbhost.changeOwner', 'Změnit ownera')}
                </Button>
              </div>
            )
            : (
              <div className='space-y-3'>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <Label>{t('dbhost.charset', 'Charset')}</Label>
                    <Input
                      size='sm'
                      value={charset}
                      onChange={(e) => setCharset(e.target.value)}
                      placeholder={detail?.charset ?? 'utf8mb4'}
                    />
                  </div>
                  <div>
                    <Label>{t('dbhost.collationOptional', 'Collation (volitelná)')}</Label>
                    <Input
                      size='sm'
                      value={collation}
                      onChange={(e) => setCollation(e.target.value)}
                      placeholder={detail?.collation ?? 'utf8mb4_unicode_ci'}
                    />
                  </div>
                </div>
                <Button
                  variant='primary'
                  disabled={!charset}
                  loading={alterMut.isPending}
                  onClick={() =>
                    alterMut.mutate({ charset, collation: collation || undefined })}
                >
                  {t('dbhost.changeCharset', 'Změnit charset')}
                </Button>
              </div>
            )}
        </div>

        {/* Danger zone */}
        <div className='space-y-3 pt-3 border-t border-gray-800'>
          <div className='text-[10px] font-semibold text-gray-600 uppercase tracking-widest'>
            Danger zone
          </div>
          <Button
            variant='danger'
            icon={<Trash2 size={15} />}
            loading={dropMut.isPending}
            onClick={async () =>
              await themedConfirm(
                t('dbhost.confirmDeleteDb', 'Smazat databázi "{{name}}"?', { name: dbName }),
              ) && dropMut.mutate()}
          >
            {t('dbhost.deleteDatabase', 'Smazat databázi')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Credentials Panel ─────────────────────────────────────────────────────────

function CredentialsPanel(
  { instance, engine, targetNode }: { instance: string; engine: DbEngine; targetNode?: string },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    username: '',
    db_name: '',
    privileges: 'ALL',
    password: '',
    entity_id: '',
  });
  const [connStrings, setConnStrings] = useState<Record<string, string>>({});
  const [adoptSel, setAdoptSel] = useState<Record<string, string>>({});

  const extra = targetNode ? { target_node: targetNode } : {};

  const { data, isLoading } = useQuery({
    queryKey: ['dbhost-creds', instance, targetNode],
    queryFn: () =>
      mcpCall<{ ok: boolean; credentials: DbCredential[] }>(
        'dbhost.credentials', { instance, action: 'list', ...extra },
      ),
  });

  // Audit — drift mezi skutečnými usery v kontejneru a ACM linky
  const auditQ = useQuery({
    queryKey: ['dbhost-creds-audit', instance, targetNode],
    queryFn: () =>
      mcpCall<DbCredAudit>('dbhost.credentials', { instance, action: 'audit', ...extra }),
  });
  const unlinked = auditQ.data?.unlinked_in_container ?? [];
  const staleSet = new Set((auditQ.data?.stale_links ?? []).map((s) => s.username));

  // Vlastnická entity — entity-access-model: DB user musí patřit entitě.
  // MCP-first (identity.entity_list); jen typy co dávají smysl jako vlastník.
  const { data: entitiesData } = useQuery({
    queryKey: ['entities-for-owner'],
    queryFn: () =>
      mcpCall<{ ok: boolean; entities: Array<{ id: string; label: string; type: string }> }>(
        'identity.entity_list',
      ),
    staleTime: 60_000,
    enabled: creating || unlinked.length > 0,
  });
  const ownerOptions = (entitiesData?.entities ?? [])
    .filter((e) => ['user', 'service', 'agent'].includes(e.type))
    .map((e) => ({ value: e.id, label: `${e.label} (${e.type})` }));

  const createMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; connection_string?: string }>('dbhost.credentials', {
        instance,
        action: 'create',
        username: form.username,
        db_name: form.db_name || undefined,
        privileges: form.privileges,
        password: form.password || undefined,
        entity_id: form.entity_id,
        ...extra,
      }),
    onSuccess: (r) => {
      toast.success(t('dbhost.userCreated', 'Uživatel "{{name}}" vytvořen', { name: form.username }));
      if (r.connection_string) {
        setConnStrings((cs) => ({ ...cs, [form.username]: r.connection_string! }));
      }
      setCreating(false);
      setForm({ username: '', db_name: '', privileges: 'ALL', password: '', entity_id: '' });
      qc.invalidateQueries({ queryKey: ['dbhost-creds', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-creds-audit', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-status', instance] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const revokeMut = useMutation({
    mutationFn: (username: string) =>
      mcpCall('dbhost.credentials', { instance, action: 'revoke', username, ...extra }),
    onSuccess: (_, username) => {
      toast.success(t('dbhost.userRevoked', '"{{name}}" odebrán', { name: username }));
      qc.invalidateQueries({ queryKey: ['dbhost-creds', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-creds-audit', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-status', instance] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const adoptMut = useMutation({
    mutationFn: (p: { username: string; entity_id: string }) =>
      mcpCall('dbhost.credentials', {
        instance,
        action: 'adopt',
        username: p.username,
        entity_id: p.entity_id,
        ...extra,
      }),
    onSuccess: (_, p) => {
      toast.success(t('dbhost.userAdopted', '"{{name}}" adoptován', { name: p.username }));
      setAdoptSel((s) => {
        const n = { ...s };
        delete n[p.username];
        return n;
      });
      qc.invalidateQueries({ queryKey: ['dbhost-creds', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-creds-audit', instance] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const connMut = useMutation({
    mutationFn: (username: string) =>
      mcpCall<{ ok: boolean; connection_string?: string }>(
        'dbhost.credentials', { instance, action: 'connection_string', username, ...extra },
      ),
    onSuccess: (r, username) => {
      if (r.connection_string) {
        setConnStrings((cs) => ({ ...cs, [username]: r.connection_string! }));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const PRIV_OPTIONS = engine === 'postgres'
    ? ['ALL', 'CONNECT', 'SELECT', 'SELECT,INSERT,UPDATE,DELETE']
    : [
      'ALL PRIVILEGES',
      'SELECT',
      'SELECT,INSERT,UPDATE,DELETE',
      'SELECT,INSERT,UPDATE,DELETE,CREATE',
    ];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm text-gray-400'>
          {t('dbhost.userCount', '{{count}} uživatelů', { count: data?.credentials?.length ?? 0 })}
        </span>
        <Button variant='primary' icon={<Plus size={15} />} onClick={() => setCreating(true)}>
          {t('dbhost.newUser', 'Nový uživatel')}
        </Button>
      </div>

      {creating && (
        <Card>
          <CardContent className='p-4 space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>{t('dbhost.usernameRequired', 'Username *')}</Label>
                <Input
                  size='sm'
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder='myapp_user'
                />
              </div>
              <div>
                <Label>{t('dbhost.dbOptionalAll', 'Databáze (prázdné = vše)')}</Label>
                <Input
                  size='sm'
                  value={form.db_name}
                  onChange={(e) => setForm((f) => ({ ...f, db_name: e.target.value }))}
                  placeholder='myapp_db'
                />
              </div>
              <div>
                <Label>{t('dbhost.privileges', 'Oprávnění')}</Label>
                <Select
                  size='sm'
                  value={form.privileges}
                  onChange={(e) => setForm((f) => ({ ...f, privileges: e.target.value }))}
                  options={PRIV_OPTIONS}
                />
              </div>
              <div>
                <Label>{t('dbhost.ownerEntity', 'Vlastník (entity) *')}</Label>
                <Select
                  size='sm'
                  value={form.entity_id}
                  onChange={(e) => setForm((f) => ({ ...f, entity_id: e.target.value }))}
                  options={ownerOptions}
                  placeholder={t('dbhost.selectEntity', '— vyber entitu —')}
                />
              </div>
              <div>
                <Label>{t('dbhost.passwordAuto', 'Heslo (prázdné = auto)')}</Label>
                <Input
                  size='sm'
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  type='password'
                  placeholder='auto-generate'
                />
              </div>
            </div>
            <div className='flex gap-2 justify-end'>
              <Button variant='ghost' onClick={() => setCreating(false)}>
                {t('common.cancel', 'Zrušit')}
              </Button>
              <Button
                variant='primary'
                onClick={() => createMut.mutate()}
                disabled={!form.username || !form.entity_id}
                loading={createMut.isPending}
              >
                {createMut.isPending
                  ? t('dbhost.creating', 'Vytvářím…')
                  : t('ui.create', 'Vytvořit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className='border border-gray-800 rounded-xl overflow-hidden'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b border-gray-800 bg-gray-900/30'>
              <th className='text-left px-4 py-2.5 text-xs text-gray-600'>{t('dbhost.usernameCol', 'Username')}</th>
              <th className='text-left px-4 py-2.5 text-xs text-gray-600'>{t('dbhost.dbCol', 'DB')}</th>
              <th className='text-left px-4 py-2.5 text-xs text-gray-600'>{t('dbhost.privileges', 'Oprávnění')}</th>
              <th className='text-left px-4 py-2.5 text-xs text-gray-600'>{t('dbhost.ownerColShort', 'Vlastník')}</th>
              <th className='px-4 py-2.5 w-20'></th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-800/40'>
            {isLoading && (
              <tr>
                <td colSpan={5} className='px-4 py-3'>
                  <div className='space-y-2'>
                    <div className='h-6 bg-gray-800/40 rounded-xl animate-pulse' />
                    <div className='h-6 bg-gray-800/40 rounded-xl animate-pulse' />
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && (data?.credentials?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    icon={<Users size={24} />}
                    title={t('dbhost.noUsers', 'Žádní uživatelé')}
                    className='py-8'
                  />
                </td>
              </tr>
            )}
            {data?.credentials?.map((cred) => (
              <>
                <tr key={cred.id} className='group hover:bg-gray-800/20'>
                  <td className='px-4 py-2.5'>
                    <span className='inline-flex items-center gap-2'>
                      <code className='text-sm font-mono text-white'>{cred.username}</code>
                      {staleSet.has(cred.username) && (
                        <span title={t('dbhost.staleUserHint', 'User v kontejneru neexistuje — smaž link (revoke)')}>
                          <Badge variant='red'>stale</Badge>
                        </span>
                      )}
                    </span>
                  </td>
                  <td className='px-4 py-2.5 text-xs text-gray-400 font-mono'>
                    {cred.db_name ?? <span className='text-gray-600'>{t('dbhost.allDbs', 'vše')}</span>}
                  </td>
                  <td className='px-4 py-2.5 text-xs text-gray-500 font-mono'>{cred.privileges}</td>
                  <td className='px-4 py-2.5 text-xs'>
                    {cred.entity_label
                      ? <span className='text-gray-300'>{cred.entity_label}</span>
                      : (
                        <span
                          className='text-amber-500/70'
                          title={t('dbhost.noOwnerHint', 'Legacy záznam bez vlastníka — adoptuj přes entitu')}
                        >
                          {t('dbhost.noOwner', 'bez vlastníka')}
                        </span>
                      )}
                  </td>
                  <td className='px-4 py-2.5'>
                    <div className='opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 justify-end'>
                      {cred.privileges === 'adopted'
                        ? (
                          <span
                            title={t('dbhost.adoptedNoPassword', 'adoptovaný link — heslo neevidováno')}
                            className='p-1 text-gray-700 cursor-not-allowed'
                          >
                            <Key size={12} />
                          </span>
                        )
                        : (
                          <button
                            onClick={() => connMut.mutate(cred.username)}
                            title={t('dbhost.showConnString', 'Zobrazit connection string')}
                            className='p-1 text-gray-600 hover:text-blue-400 transition-colors'
                          >
                            <Key size={12} />
                          </button>
                        )}
                      <button
                        onClick={async () =>
                          await themedConfirm(
                            t('dbhost.confirmRevoke', 'Odebrat "{{name}}"?', { name: cred.username }),
                          ) && revokeMut.mutate(cred.username)}
                        className='p-1 text-gray-600 hover:text-red-400 transition-colors'
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
                {connStrings[cred.username] && (
                  <tr key={`${cred.id}-conn`} className='bg-gray-900/50'>
                    <td colSpan={5} className='px-4 py-2'>
                      <div className='flex items-center gap-2'>
                        <code className='text-xs font-mono text-green-400 flex-1 overflow-x-auto'>
                          {connStrings[cred.username]}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(connStrings[cred.username]);
                            toast.success(t('common.copied', 'Zkopírováno'));
                          }}
                          className='p-1 text-gray-600 hover:text-white transition-colors flex-shrink-0'
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          onClick={() =>
                            setConnStrings((cs) => {
                              const n = { ...cs };
                              delete n[cred.username];
                              return n;
                            })}
                          className='p-1 text-gray-600 hover:text-white transition-colors flex-shrink-0'
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Neevidovaní useři v kontejneru — adopce na entitu */}
      {unlinked.length > 0 && (
        <Card className='border-amber-500/20 bg-amber-950/10'>
          <CardContent className='p-4 space-y-3'>
            <div className='flex items-center gap-2 text-sm font-semibold text-amber-400'>
              <AlertTriangle size={14} />
              {t('dbhost.unlinkedUsers', 'Neevidovaní useři v kontejneru ({{count}})', {
                count: unlinked.length,
              })}
            </div>
            <div className='space-y-2'>
              {unlinked.map((u) => (
                <div key={u.username} className='flex items-center gap-3'>
                  <code className='text-xs font-mono text-white flex-shrink-0'>{u.username}</code>
                  <span className='text-xs text-gray-600 flex-1 truncate'>
                    {u.hosts?.join(', ')}
                  </span>
                  <Select
                    size='sm'
                    className='w-48'
                    options={ownerOptions}
                    placeholder={t('dbhost.selectEntity', '— vyber entitu —')}
                    value={adoptSel[u.username] ?? ''}
                    onChange={(e) =>
                      setAdoptSel((s) => ({ ...s, [u.username]: e.target.value }))}
                  />
                  <Button
                    size='sm'
                    variant='secondary'
                    disabled={!adoptSel[u.username]}
                    loading={adoptMut.isPending && adoptMut.variables?.username === u.username}
                    onClick={() =>
                      adoptMut.mutate({
                        username: u.username,
                        entity_id: adoptSel[u.username]!,
                      })}
                  >
                    {t('dbhost.adopt', 'Adoptovat')}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── SQL Exec Panel ────────────────────────────────────────────────────────────

function SqlExecPanel(
  { instance, engine, targetNode }: { instance: string; engine: DbEngine; targetNode?: string },
) {
  const { t } = useTranslation();
  const [sql, setSql] = useState('SELECT version();');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);

  const extra = targetNode ? { target_node: targetNode } : {};

  const PRESETS = engine === 'postgres'
    ? [
      { label: 'version', sql: 'SELECT version();' },
      { label: 'databases', sql: 'SELECT datname FROM pg_database ORDER BY datname;' },
      { label: 'users', sql: 'SELECT usename, usesuper FROM pg_user;' },
      { label: 'connections', sql: 'SELECT count(*), state FROM pg_stat_activity GROUP BY state;' },
      {
        label: 'db sizes',
        sql:
          'SELECT datname, pg_size_pretty(pg_database_size(datname)) FROM pg_database ORDER BY pg_database_size(datname) DESC;',
      },
    ]
    : [
      { label: 'version', sql: 'SELECT version();' },
      { label: 'databases', sql: 'SHOW DATABASES;' },
      { label: 'users', sql: 'SELECT User, Host FROM mysql.user;' },
      { label: 'status', sql: 'SHOW STATUS LIKE "Threads_connected";' },
      { label: 'processlist', sql: 'SHOW PROCESSLIST;' },
    ];

  const run = async () => {
    if (!sql.trim()) return;
    setRunning(true);
    setOutput('');
    try {
      const r = await mcpCall<{ ok: boolean; output?: string; error?: string }>(
        'dbhost.exec', { instance, sql, ...extra },
      );
      setOutput(
        r.ok
          ? (r.output ?? t('dbhost.emptyResult', '(prázdný výsledek)'))
          : `✗ ${r.error}`,
      );
    } catch (e) {
      setOutput(t('chat.error', 'Chyba: {{message}}', { message: (e as Error).message }));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap gap-1.5'>
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            variant='ghost'
            onClick={() => setSql(p.sql)}
            className='border border-gray-700 font-mono'
          >
            {p.label}
          </Button>
        ))}
      </div>
      <div className='space-y-2'>
        <Textarea
          size='sm'
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          rows={4}
          className='font-mono resize-none'
          placeholder='SELECT ...'
        />
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-600'>
            {t('dbhost.sqlReadOnlyHint', 'Pouze SELECT / SHOW / DESCRIBE / EXPLAIN')}
          </span>
          <div className='flex-1' />
          <Button
            variant='primary'
            size='md'
            icon={<Terminal size={15} />}
            loading={running}
            onClick={run}
            disabled={!sql.trim()}
          >
            {running ? t('dbhost.runningSql', 'Spouštím…') : t('tasks.runTitle', 'Spustit')}
          </Button>
        </div>
      </div>
      {output && (
        <pre className='bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs font-mono text-green-400 overflow-x-auto max-h-80 overflow-y-auto whitespace-pre-wrap'>
          {output}
        </pre>
      )}
    </div>
  );
}

// ── Logs Panel ────────────────────────────────────────────────────────────────

function LogsPanel({ instance, targetNode }: { instance: string; targetNode?: string }) {
  const { t } = useTranslation();
  const [lines, setLines] = useState(100);

  const extra = targetNode ? { target_node: targetNode } : {};

  const { data, isLoading, refetch: refresh } = useQuery({
    queryKey: ['dbhost-logs', instance, targetNode, lines],
    queryFn: () =>
      mcpCall<{ ok: boolean; logs: string }>(
        'dbhost.manage', { name: instance, action: 'logs', lines, ...extra },
      ),
  });

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2 justify-end'>
        <Select
          size='sm'
          value={String(lines)}
          onChange={(e) => setLines(Number(e.target.value))}
          options={[50, 100, 200, 500].map((n) => ({
            value: String(n),
            label: t('dbhost.linesCount', '{{count}} řádků', { count: n }),
          }))}
          className='w-32'
        />
        <RefreshButton onRefresh={() => refresh()} loading={isLoading} size='md' />
      </div>
      {isLoading && <div className='h-40 bg-gray-800/40 rounded-xl animate-pulse' />}
      {data && (
        <pre className='bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs font-mono text-gray-300 overflow-x-auto max-h-[500px] overflow-y-auto whitespace-pre-wrap'>
          {data.logs || t('dbhost.emptyLogs', '(prázdné logy)')}
        </pre>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

interface DbInstancePanelProps {
  /** Název DB instance (dbhost_instances.name) */
  instance: string;
  /** Engine databáze */
  engine: DbEngine;
  /** Volitelný ACM node (name/label/IPv6/IPv4) — prázdné = lokální */
  targetNode?: string;
  /** Počet databází a uživatelů pro nadpisy záložek (volitelně) */
  dbCount?: number;
  credCount?: number;
}

export function DbInstancePanel(
  { instance, engine, targetNode, dbCount, credCount }: DbInstancePanelProps,
) {
  const { t } = useTranslation();
  return (
    <Tabs defaultTab='databases'>
      <TabList>
        <Tab id='databases'>
          <span className='flex items-center gap-1.5'>
            <Database size={15} />
            {t('dbhost.tabDatabases', 'Databáze')}{' '}
            {dbCount !== undefined && <span className='text-gray-500'>({dbCount})</span>}
          </span>
        </Tab>
        <Tab id='credentials'>
          <span className='flex items-center gap-1.5'>
            <Users size={15} />
            {t('dbhost.tabCredentials', 'Přihlášení')}{' '}
            {credCount !== undefined && <span className='text-gray-500'>({credCount})</span>}
          </span>
        </Tab>
        <Tab id='exec'>
          <span className='flex items-center gap-1.5'>
            <Terminal size={15} />{t('dbhost.tabSqlExec', 'SQL Exec')}
          </span>
        </Tab>
        <Tab id='logs'>{t('dbhost.tabLogs', 'Logy')}</Tab>
        {engine === 'mariadb' && (
          <Tab id='perf'>
            <span className='flex items-center gap-1.5'>
              <BarChart3 size={15} />{t('dbhost.tabPerf', 'Výkon')}
            </span>
          </Tab>
        )}
      </TabList>
      <TabPanel id='databases'>
        <DatabasesPanel instance={instance} engine={engine} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='credentials'>
        <CredentialsPanel instance={instance} engine={engine} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='exec'>
        <SqlExecPanel instance={instance} engine={engine} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='logs'>
        <LogsPanel instance={instance} targetNode={targetNode} />
      </TabPanel>
      {engine === 'mariadb' && (
        <TabPanel id='perf'>
          <MariaDbPerfPanel instance={instance} targetNode={targetNode} />
        </TabPanel>
      )}
    </Tabs>
  );
}
